import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Zap, ShieldCheck, Sprout, Warehouse } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { HERO } from "@/constants/testIds";

const orbitNodes = [
  { icon: Sprout, label: "soil", angle: 0, color: "#10B981" },
  { icon: Warehouse, label: "garage", angle: 72, color: "#F59E0B" },
  { icon: ShieldCheck, label: "security", angle: 144, color: "#EF4444" },
  { icon: Zap, label: "motion", angle: 216, color: "#06B6D4" },
];

export default function Hero() {
  const { t } = useApp();
  return (
    <section
      data-testid={HERO.root}
      className="relative overflow-hidden pt-16 sm:pt-24 pb-20 sm:pb-32"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-10 grid md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-7">
          <motion.div
            data-testid={HERO.badge}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase font-bold text-amber-300 bg-amber-500/10 border border-amber-500/25 rounded-full px-3 py-1.5 mb-6"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-amber-400 pulse-glow" />
            {t.hero.badge}
          </motion.div>

          <h1 className="font-heading font-medium text-5xl sm:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tighter">
            <motion.span
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="block"
            >
              {t.hero.title1}
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.18 }}
              className="block text-amber-400 italic font-light"
              style={{ fontFamily: "Outfit" }}
            >
              {t.hero.title2}
            </motion.span>
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="mt-7 text-base sm:text-lg text-gray-300 leading-relaxed max-w-xl"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.45 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <Link
              to="/scenarios"
              data-testid={HERO.primaryCta}
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full px-7 py-3.5 transition-all hover:shadow-[0_0_30px_rgba(245,158,11,0.45)]"
            >
              {t.hero.ctaPrimary}
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/builder"
              data-testid={HERO.secondaryCta}
              className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white rounded-full px-7 py-3.5 transition-colors"
            >
              {t.hero.ctaSecondary}
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-14 grid grid-cols-3 gap-6 max-w-md"
          >
            {t.hero.stats.map((s) => (
              <div key={s.v}>
                <div className="font-mono-tight text-3xl sm:text-4xl font-medium text-white">
                  {s.k}
                </div>
                <div className="text-[11px] mt-1 uppercase tracking-[0.18em] text-gray-500">
                  {s.v}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right side orbit visual */}
        <div className="md:col-span-5">
          <HeroOrbit />
        </div>
      </div>
    </section>
  );
}

function HeroOrbit() {
  return (
    <div className="relative h-[420px] w-full">
      <div className="absolute inset-0 flex items-center justify-center">
        {/* Concentric rings */}
        {[1, 2, 3].map((r) => (
          <motion.div
            key={r}
            className="absolute rounded-full border border-white/[0.08]"
            style={{ width: r * 130, height: r * 130 }}
            animate={{ rotate: r % 2 === 0 ? 360 : -360 }}
            transition={{ duration: 60 + r * 10, repeat: Infinity, ease: "linear" }}
          />
        ))}
        {/* Center hub */}
        <motion.div
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="relative h-24 w-24 rounded-3xl bg-amber-500/15 border border-amber-500/40 flex items-center justify-center"
        >
          <div className="absolute inset-0 rounded-3xl pulse-glow" />
          <div className="font-heading font-semibold text-amber-300 text-sm tracking-wider">HUB</div>
        </motion.div>
        {/* Orbit nodes */}
        {orbitNodes.map((node, i) => {
          const radius = 175;
          const rad = (node.angle * Math.PI) / 180;
          const x = Math.cos(rad) * radius;
          const y = Math.sin(rad) * radius;
          const Icon = node.icon;
          return (
            <div
              key={node.label}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
              style={{ marginLeft: x, marginTop: y }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.4 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }}
                className="h-14 w-14 rounded-2xl flex items-center justify-center backdrop-blur-md"
                style={{
                  background: `${node.color}1A`,
                  border: `1px solid ${node.color}55`,
                  color: node.color,
                }}
              >
                <Icon className="h-5 w-5" strokeWidth={2} />
              </motion.div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
